<template>
    <div v-if="displayProfileCTA" :class="{'profile-cta': $ff('profile-revamp'), 'profile-cta-old': !$ff('profile-revamp')}">
        <AnswerTimeIndicator
            v-if="profile?.conversationStatistics.averageAnswerTimeCode && $ff('profile-revamp')"
            :average-answer-time-code="profile?.conversationStatistics.averageAnswerTimeCode"
        />
        <ClientOnly v-if="currentIdentityIsClient">
            <ProjectProposalProductTour />
            <!-- Empty fallback to prevent content layout shift -->
            <template #fallback></template>
        </ClientOnly>
        <VJoyButton
            v-if="!comesFromSearch && !shortlist"
            class="profile-cta-project"
            variant="main"
            :size="$ff('profile-revamp') ? 'small' : 'medium'"
            :full="$ff('profile-revamp')"
            data-testid="contact-button-project-proposal"
            data-client-onboarding-propose-a-project
            :disabled="isCtaDisabled"
            @click="redirectToProjectProposal"
        >
            {{ contactCtaLabel }}
        </VJoyButton>
        <VJoyButton
            v-else-if="hasShortlistItems && !profileInShortlist"
            class="profile-cta-project"
            variant="main"
            :size="$ff('profile-revamp') ? 'small' : 'medium'"
            :loading="loading"
            :full="$ff('profile-revamp')"
            data-testid="contact-button-in-shortlist"
            :disabled="isCtaDisabled"
            @click="handleAddToShortlist"
        >
            {{ t('profile.shortlist.add.button') }}
        </VJoyButton>
        <VJoyButton
            v-else-if="hasShortlistItems && profileInShortlist"
            class="profile-cta-project"
            variant="main"
            :size="$ff('profile-revamp') ? 'small' : 'medium'"
            :loading="loading"
            :full="$ff('profile-revamp')"
            data-testid="contact-button-remove-shortlist"
            :disabled="isCtaDisabled"
            @click="handleRemoveFromShortlist"
        >
            {{ t('profile.shortlist.remove.button') }}
        </VJoyButton>
        <div v-else>
            <ExternalUserActionShortlistModeSelection />
            <VJoyButton
                class="profile-cta-project"
                variant="main"
                :size="$ff('profile-revamp') ? 'small' : 'medium'"
                :full="$ff('profile-revamp')"
                data-testid="contact-button-shortlist"
                data-client-onboarding-propose-a-project
                :disabled="isCtaDisabled"
                @click="displayChoice"
            >
                {{ contactCtaLabel }}
            </VJoyButton>
        </div>
        <InstantConversationDialog :search-event-id="searchEventId" />
        <template v-if="!$ff('profile-revamp') && displayInstantConversationUsage && instantConversationUsage">
            <small v-if="instantConversationLimitReached" class="profile-instant-conversation-subtitle">
                {{ t('profile.instant.conversation.usage.limit.reached', [instantConversationUsage.usage, instantConversationUsage.limit]) }}
                <span class="profile-instant-conversation-subtitle--subtext">
                    {{ t('profile.instant.conversation.usage.limit.reached.subtext') }}
                </span>
            </small>
            <small v-else class="profile-instant-conversation-subtitle">
                {{ t('profile.instant.conversation.usage', [instantConversationUsage.usage, instantConversationUsage.limit]) }}
            </small>
        </template>
        <small v-if="!$ff('profile-revamp')" class="profile-cta-subtitle">
            {{ t('profile.project.submission.title', [profile!.firstname]) }}
        </small>
    </div>
    <div v-else-if="!$ff('profile-revamp')" class="profile-cta-old">
        <div class="profile-cta-project"></div>
    </div>
</template>

<script setup lang="ts">
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {computed, ref, useAuth, useDataTracking, useHydrationState, useTranslation} from '#imports';
    import {useABTest, useFeatureFlag} from '@malt/feature-flags';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyButton} from '@maltjoy/core-vue';
    import ExternalUserActionShortlistModeSelection from '~/components/external-user-action/ExternalUserActionShortlistModeSelection.vue';
    import {useComesFromAdvancedSearch} from '~/composables/useComesFromAdvancedSearch';
    import {eventBus} from '~/config/events';
    import {useInstantConversationStore} from '~/store/instantConversation.store';
    import {useProfileStore} from '~/store/profileStore';
    import AnswerTimeIndicator from './indicators/AnswerTimeIndicator.vue';
    import InstantConversationDialog from './InstantConversationDialog.vue';
    import ProjectProposalProductTour from './ProjectProposalProductTour.vue';
    import {useShortlistStore} from '@/store/shortlist/ShortlistStore';

    // Composables
    const {t} = useTranslation();
    const route = useRoute();
    const {isHydrated} = useHydrationState();

    // Stores
    const {currentIdentityIsClient, loggedIn} = storeToRefs(useAuth());
    const shortlistStore = useShortlistStore();
    const {profile} = storeToRefs(useProfileStore());
    const {shortlist, hasShortlistItems, profileInShortlist, comesFromSearch} = storeToRefs(shortlistStore);
    const {instantConversationUsage, instantConversationLimitReached} = storeToRefs(useInstantConversationStore());
    const isProfileRevampEnabled = useFeatureFlag('profile-revamp');
    const isClientInstantConversationEnabled = useFeatureFlag('client-instant-conversation');
    const isLimitInstantConversationPerDayEnabled = useFeatureFlag('limit-number-of-instant-conversation-per-day');
    const {getVariationWithoutTracking} = useABTest();

    // Refs
    const loading = ref(false);

    // Computed values
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);
    const isCtaDisabled = computed(() => isInOverviewMode.value || !isHydrated.value);
    const comesFromAdvancedSearch = useComesFromAdvancedSearch();
    const searchEventId = computed<string | undefined>(() => route?.query?.searchid as string);
    const displayProfileCTA = computed(
        () => !profile.value?.editMode && !comesFromAdvancedSearch.value && (!isInOverviewMode.value || isProfileRevampEnabled.value),
    );
    const contactCtaLabel = t('show.contactme.button.label');
    const displayInstantConversationUsage = computed(
        () =>
            isClientInstantConversationEnabled.value &&
            isLimitInstantConversationPerDayEnabled.value &&
            loggedIn.value &&
            currentIdentityIsClient.value &&
            getVariationWithoutTracking('instant-conversation-v2') === 'treatment',
    );

    async function handleAddToShortlist() {
        loading.value = true;
        const {track} = useDataTracking();
        const route = useRoute();
        const searchId = route?.query?.searchid as string;
        const sourceComponent = route?.query?.sourceComponent as string;

        const redirectUrl = await shortlistStore.add();

        if (redirectUrl) {
            track('freelancer_shortlisted', {
                search_event_id: searchId,
                freelancer_account_id: profile.value!.accountId,
            });
            location.href = `${redirectUrl}${sourceComponent ? `&sourceComponent=${sourceComponent}` : ''}`;
        }
        loading.value = false;
    }

    async function handleRemoveFromShortlist() {
        loading.value = true;
        await shortlistStore.remove();
        loading.value = false;
    }

    function displayChoice() {
        eventBus.emit('shortlist:display-choice');
    }

    function redirectToProjectProposal() {
        const {track} = useDataTracking();

        track('profile_action_cta_clicked', {
            freelancer_id: profile.value!.profileId,
            action_type: 'start_a_brief',
            search_id: searchEventId.value,
        });

        return safelyNavigateTo(
            {
                path: '/client/briefs/proposal',
                query: {
                    searchId: route?.query?.searchid,
                    profileViewId: profile.value!.profileViewId,
                    profileIds: profile.value!.profileId,
                    sourceComponent: route?.query?.sourceComponent,
                    entryPoint: 'profile_page',
                },
            },
            {externalToThisApp: true},
        );
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-cta {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--joy-core-spacing-4);
        width: 100%;
    }

    // TODO: to remove with ff profile-revamp
    .profile-cta-old {
        width: 200px;
        height: 100%;

        display: flex;
        flex-direction: column;
        align-items: flex-start;

        @include mq.screen_md {
            width: auto;
            flex-direction: row;
            gap: var(--joy-core-spacing-8);
        }
        @include mq.screen_xs {
            background-color: var(--joy-color-neutral-0);
            position: fixed;
            width: 100%;
            height: initial;
            bottom: 0;
            z-index: calc(var(--header-z-index) - 1);
            padding: 10px 20px 0 20px;
            right: 0;
            display: flex;
            flex-direction: column-reverse;
            align-items: flex-start;
            gap: var(--joy-core-spacing-4);
            .profile-cta-subtitle {
                display: none;
            }
        }

        .profile-cta {
            &-project {
                margin-bottom: var(--joy-core-spacing-2);
                width: 100%;
            }

            &-subtitle {
                margin-right: auto;
                margin-left: auto;
                margin-top: var(--joy-core-spacing-2);
                width: 100%;
                text-overflow: ellipsis;
                overflow: hidden;
            }
        }

        .profile-instant-conversation-subtitle {
            margin: 0 auto;
            text-align: center;
            font-weight: var(--joy-font-weight-bold);

            @include mq.screen_md(min) {
                margin: unset;
                text-align: left;
            }

            &--subtext {
                display: block;
                font-weight: var(--joy-font-weight-normal);
            }
        }
    }
</style>
